import { Fade, Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import useShowSnackbar from 'hooks/useShowSnackbar'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useUpdateNoteMutation } from 'store/modules/PatientNotes'
import { trackEvent } from 'utils/analyticsUtils'
import CreatorInfo from './CreatorInfo'
import DeleteNoteModal from './DeleteNoteModal'
import NoteContent from './NoteContent'
import NoteItemActions from './NoteItemActions'
import { PinNoteIcon } from 'components/common/icons'
import { NoteTypes, UserNoteVisiblities } from 'logic/patientNotesLogic'

const useStyles = makeStyles(theme => ({
  noteItemRoot: {
    position: 'relative',
    width: '100%',
    backgroundColor: ({ isPublic }) => (isPublic ? '#F3F3F4' : 'rgba(235, 238, 255, 1)'),
    borderRadius: 10,
    padding: 8,
    marginBottom: 12,
    transition: 'background-color 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: ({ isPublic }) => (isPublic ? '#e5e5e5' : '#E1E4FA')
    }
  }
}))

const NoteItem = React.forwardRef(
  (
    {
      id,
      body,
      attachments,
      patientGrinUserId,
      isPinned,
      metadata,
      createdAt,
      visibility = UserNoteVisiblities.Public,
      withPinIndication = true
    },
    ref
  ) => {
    const { t } = useTranslation()
    const doctor = useSelector(state => state.profileReducer.doctor)

    const [isHovered, setIsHovered] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [currentBody, setCurrentBody] = useState()
    const [currentHINote, setCurrentHINote] = useState()
    const [isDeleteNoteModalOpen, setIsDeleteModalOpen] = useState(false)

    const parsedMetadata = JSON.parse(metadata || '{}')

    const isOwnedByCurrentUser = useMemo(
      () => doctor.user.id === parsedMetadata.creatorUserId,
      [doctor, parsedMetadata]
    )
    const isNeedsAttentionNote = useMemo(
      () =>
        parsedMetadata.noteType === NoteTypes.NeedsAttention ||
        parsedMetadata.noteType === NoteTypes.NeedsAttentionResolved,
      [parsedMetadata]
    )
    const isEditable = useMemo(
      () => isOwnedByCurrentUser && !isNeedsAttentionNote,
      [isOwnedByCurrentUser, isNeedsAttentionNote]
    )

    const shouldDisplayActions = useMemo(() => isHovered && !isEditMode, [isEditMode, isHovered])

    const [updateNote, { isError: isFailedUpdatingNote }] = useUpdateNoteMutation()
    useShowSnackbar({
      isOpen: isFailedUpdatingNote,
      message: t('pages.patients.patientSwitchableView.notes.generalError')
    })

    const handleToggleEditMode = useCallback(() => {
      trackEvent('Patient notes - Edit mode toggled', {
        toggle: !isEditMode ? 'off' : 'true'
      })
      setIsEditMode(!isEditMode)
    }, [isEditMode])

    const handleBodySaved = useCallback(async () => {
      trackEvent('Patient notes - New Body saved', { patientGrinUserId })
      setIsEditMode(!isEditMode)
      await updateNote({
        id,
        variables: {
          body: currentBody,
          metadata: JSON.stringify({ ...parsedMetadata, hiNote: currentHINote }),
          grinUserId: patientGrinUserId
        }
      })
    }, [patientGrinUserId, isEditMode, updateNote, id, currentBody, parsedMetadata, currentHINote])

    const handleUpdateVisibility = useCallback(
      async value => {
        trackEvent('Patient notes - Note visibility updated', { visibilityValue: value ?? 'public', patientGrinUserId })
        await updateNote({ id, variables: { visibility: value, grinUserId: patientGrinUserId } })
      },
      [patientGrinUserId, id, updateNote]
    )

    const handleBodyDiscarded = useCallback(() => {
      trackEvent('Patient notes - Body discarded')
      setCurrentBody(body)
      setIsEditMode(false)
    }, [body])

    const handleDeleteNoteClicked = useCallback(() => {
      trackEvent('Patient notes - Delete note modal opened')
      setIsDeleteModalOpen(true)
    }, [])

    const handleTogglePinnedNote = useCallback(async () => {
      trackEvent('Patient note: Note pin toggled', {
        noteId: id,
        isPinned: !isPinned,
        source: 'Switchable view'
      })
      await updateNote({ id, variables: { isPinned: !isPinned, grinUserId: patientGrinUserId } })
    }, [patientGrinUserId, id, isPinned, updateNote])

    const handleCopyNoteId = useCallback(() => navigator.clipboard.writeText(id), [id])

    useEffect(() => {
      setCurrentBody(body)
    }, [body])

    useEffect(() => {
      if (parsedMetadata?.hiNote && !currentHINote) {
        setCurrentHINote(parsedMetadata?.hiNote)
      }
    }, [currentHINote, parsedMetadata?.hiNote])

    const classes = useStyles({ isPublic: visibility === UserNoteVisiblities.Public })

    return (
      <>
        <Grid
          container
          direction="column"
          className={classes.noteItemRoot}
          ref={ref}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Grid item>
            <Grid container direction="row" justifyContent="space-between">
              <Grid item style={{ flex: 1 }}>
                <CreatorInfo
                  userId={parsedMetadata.creatorUserId}
                  noteParsedMetadata={parsedMetadata}
                  createdAt={createdAt}
                  visibility={visibility}
                  onVisibilitySelected={handleUpdateVisibility}
                  isVisibilityEditable={isEditable && !isNeedsAttentionNote}
                />
              </Grid>
              {withPinIndication && isPinned && (
                <Grid item>
                  <PinNoteIcon color="#999" />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item style={{ width: '100%' }}>
            <NoteContent
              isEditMode={isEditMode}
              currentHINote={currentHINote}
              setCurrentHINote={setCurrentHINote}
              body={currentBody}
              attachments={attachments}
              noteParsedMetadata={parsedMetadata}
              setBody={setCurrentBody}
              onBodySaved={handleBodySaved}
              onBodyDiscarded={handleBodyDiscarded}
              isHovered={isHovered}
            />
          </Grid>
          <Fade in={shouldDisplayActions} timeout={500}>
            <Grid item>
              <NoteItemActions
                isEditable={isEditable}
                isDeletable={isOwnedByCurrentUser}
                isPinned={isPinned}
                onToggleEditMode={handleToggleEditMode}
                onDeleteNoteClicked={handleDeleteNoteClicked}
                onTogglePinnedNoteClicked={handleTogglePinnedNote}
                onCopyIdClicked={handleCopyNoteId}
              />
            </Grid>
          </Fade>
        </Grid>
        {isDeleteNoteModalOpen && (
          <DeleteNoteModal isOpen={isDeleteNoteModalOpen} setIsOpen={setIsDeleteModalOpen} noteId={id} />
        )}
      </>
    )
  }
)

export default NoteItem
